import React, { type ComponentProps, memo, useCallback, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  interpolate,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { Image } from '@garnish/media';

import { AppIcon } from '../../assets';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPromptAppIcon = memo((props: Props) => {
  const { entering, opacitySV } = props;

  // ─── Animations ──────────────────────────────────────────────────────

  const radarOpacitySV = useSharedValue(0);
  const radarProgressSV = useSharedValue(0);

  // ─── Styles ──────────────────────────────────────────────────────────

  const wrapperAnimatedStyles = useAnimatedStyle(() => {
    return opacitySV ? { opacity: opacitySV.value } : {};
  });

  const radarStyles = [StyleSheet.absoluteFill];

  // ─── Handlers ────────────────────────────────────────────────────────

  const triggerAnimations = useCallback(() => {
    radarOpacitySV.value = withDelay(
      RADAR_INTRO_DELAY,
      withTiming(1, { duration: RADAR_INTRO_DURATION }),
    );

    radarProgressSV.value = withDelay(
      RADAR_INTRO_DELAY + RADAR_INTRO_DURATION + 1000,
      withRepeat(withTiming(1, { duration: RADAR_CYCLE_DURATION, easing: Easing.linear }), -1),
    );
  }, [radarOpacitySV, radarProgressSV]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(triggerAnimations, [triggerAnimations]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View entering={entering} style={wrapperAnimatedStyles}>
      <View style={styles.imageContainer}>
        <Image source={AppIcon} style={styles.image} contentFit="contain" accessible={false} />

        <Animated.View
          style={radarStyles}
          entering={FadeIn.duration(theme.transitions.long).delay(1250)}
        >
          {RADAR_STROKES.map((stroke, index) => (
            <RadarStroke
              key={index}
              size={stroke.size}
              opacityInput={stroke.opacityInput}
              progressSV={radarProgressSV}
            />
          ))}

          <View style={styles.dot} />
        </Animated.View>
      </View>
    </Animated.View>
  );
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const RadarStroke = memo((params: RadarStrokeParams) => {
  const { size, opacityInput, progressSV } = params;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({
      width: size,
      height: size,
      borderRadius: size / 2,
      top: RADAR_ANCHOR_TOP - size / 2,
      right: RADAR_ANCHOR_RIGHT - size / 2,
    }),
    [size],
  );

  const containerAnimatedStyles = useAnimatedStyle(() => ({
    opacity: interpolate(progressSV.value, opacityInput, RADAR_OPACITY_OUTPUT),
  }));

  const containerStyles = [styles.stroke, containerDynamicStyles, containerAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      style={containerStyles}
      accessible={false}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    />
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DOT_SIZE = 34;

const RADAR_ANCHOR_TOP = 45;
const RADAR_ANCHOR_RIGHT = 55;

const RADAR_STROKES_COUNT = 5;
const RADAR_STROKE_MIN_SIZE = 60;
const RADAR_STROKE_MAX_SIZE = 288;

const RADAR_INTRO_DELAY = 500;
const RADAR_INTRO_DURATION = 350;

const STROKE_FADE_UP_DURATION = 400;
const STROKE_FADE_DOWN_DURATION = 500;
const STROKE_REST_DURATION = 500;

const RADAR_CYCLE_DURATION = RADAR_STROKES_COUNT * STROKE_FADE_UP_DURATION + STROKE_FADE_DOWN_DURATION + STROKE_REST_DURATION; // prettier-ignore
const RADAR_FADE_OUT_START = (RADAR_STROKES_COUNT * STROKE_FADE_UP_DURATION) / RADAR_CYCLE_DURATION;
const RADAR_FADE_OUT_END = RADAR_FADE_OUT_START + STROKE_FADE_DOWN_DURATION / RADAR_CYCLE_DURATION;
const RADAR_OPACITY_OUTPUT = [0, 0, 1, 1, 0, 0] as const;

const RADAR_STROKE_SIZE_RATIO =
  (RADAR_STROKE_MAX_SIZE / RADAR_STROKE_MIN_SIZE) ** (1 / (RADAR_STROKES_COUNT - 1));

const RADAR_STROKES = Array.from({ length: RADAR_STROKES_COUNT }, (_, index) => {
  const fadeInStart = (index * STROKE_FADE_UP_DURATION) / RADAR_CYCLE_DURATION;
  const fadeInEnd = ((index + 1) * STROKE_FADE_UP_DURATION) / RADAR_CYCLE_DURATION;

  return {
    size: RADAR_STROKE_MIN_SIZE * RADAR_STROKE_SIZE_RATIO ** index,
    opacityInput: [0, fadeInStart, fadeInEnd, RADAR_FADE_OUT_START, RADAR_FADE_OUT_END, 1],
  };
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  imageContainer: {
    width: 193,
    height: 199,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  stroke: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: theme.colors.LIME,
  },
  dot: {
    position: 'absolute',
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
    top: RADAR_ANCHOR_TOP - DOT_SIZE / 2,
    right: RADAR_ANCHOR_RIGHT - DOT_SIZE / 2,
    backgroundColor: theme.colors.LIME,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  entering?: ComponentProps<typeof Animated.View>['entering'];
  opacitySV?: SharedValue<number>;
};

type RadarStrokeParams = {
  size: number;
  opacityInput: readonly number[];
  progressSV: SharedValue<number>;
};
